import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import {
  FaPlusCircle,
  FaCalendarAlt,
  FaChevronRight,
  FaFolderOpen,
  FaBullhorn,
  FaBirthdayCake,
  FaCheckCircle,
  FaBell,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { authFetch } from "../utils/api";
import { hasRole, ADMIN_ROLES } from "../config/roles";
import useIsMobile from "../hooks/useIsMobile";
import { isStandalonePwa } from "../utils/pwa";
import { notificationPermissionState, requestNotificationPermission, subscribeToPush } from "../utils/push";

/* ----------------------------------------------------------------------------
   CONFIG
-----------------------------------------------------------------------------*/
const ABSOLUTE_HOST = "https://dev.sliitleo.org";

/* ----------------------------------------------------------------------------
   HELPERS
-----------------------------------------------------------------------------*/
function readUserFromStorage() {
  try {
    const raw = JSON.parse(localStorage.getItem("user")) || {};
    if (raw?.image && typeof raw.image === "string" && !raw.image.startsWith("http")) {
      raw.image = `${ABSOLUTE_HOST}${raw.image.startsWith("/") ? "" : "/"}${raw.image}`;
    }
    return { name: "Unknown User", role: "Member", image: "/user.png", ...raw };
  } catch {
    return { name: "Unknown User", role: "Member", image: "/user.png" };
  }
}
function splitName(full = "") {
  const parts = full.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "User", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts[0], last: parts.slice(1).join(" ") };
}
async function parseJsonSafe(res) {
  const t = await res.text();
  try {
    return JSON.parse(t);
  } catch {
    throw new Error(t.slice(0, 400));
  }
}

/* ----------------------------------------------------------------------------
   SKELETON
-----------------------------------------------------------------------------*/
function ProjectSkeletonCard() {
  return (
    <div className="panel" style={{ padding: "var(--sp-4)" }}>
      <div className="skeleton" style={{ height: 14, width: "65%", marginBottom: 10 }} />
      <div className="skeleton" style={{ height: 11, width: "40%", marginBottom: 16 }} />
      <div className="skeleton" style={{ height: 34, width: "100%" }} />
    </div>
  );
}

/* ----------------------------------------------------------------------------
   STATUS STYLES
-----------------------------------------------------------------------------*/
function statusStyle(status = "") {
  const s = status.toLowerCase();
  if (/(done|complete|completed|success|approved)/i.test(status))
    return "bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30";
  if (/(pending|planned|draft|on hold|waiting)/i.test(status))
    return "bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30";
  if (/(blocked|failed|cancel|rejected|issue)/i.test(status))
    return "bg-rose-500/20 text-rose-300 ring-1 ring-rose-500/30";
  return "bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/30";
}

/* Maps a project's raw status text to one of the shared `.pill-*` variants
   from shell.css so status badges look the same everywhere in the app. */
function projectPillClass(status = "") {
  const s = status.toLowerCase();
  if (/(done|complete|completed|success|approved)/i.test(s)) return "pill-success";
  if (/(cancel|rejected)/i.test(s)) return "pill-danger";
  if (/(hold|block)/i.test(s)) return "pill-warning";
  return "pill-info";
}

/* ----------------------------------------------------------------------------
   EVENT HELPERS (support ISO `start` or DB `event_date`/`start_time`)
-----------------------------------------------------------------------------*/
function getEventStartDate(ev) {
  if (ev?.start) {
    const d = new Date(String(ev.start));
    if (!isNaN(d)) return d;
  }
  if (ev?.event_date) {
    const t = ev.start_time || "00:00:00";
    const d = new Date(`${String(ev.event_date).trim()}T${t}`);
    if (!isNaN(d)) return d;
  }
  if (ev?.end) {
    const d = new Date(String(ev.end));
    if (!isNaN(d)) return d;
  }
  return null;
}
function getEventEndTimeStr(ev) {
  if (ev?.end && /T\d{2}:\d{2}/.test(ev.end)) {
    const d = new Date(String(ev.end));
    if (!isNaN(d)) return d.toTimeString().slice(0, 5);
  }
  if (ev?.end_time) return String(ev.end_time).slice(0, 5);
  return "";
}
function formatEventDateTimeFromEvent(ev) {
  const start = getEventStartDate(ev);
  if (!start) return "";
  const day = start.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
  const st = start.toTimeString().slice(0, 5);
  const et = getEventEndTimeStr(ev);
  if (st && et) return `${day} • ${st}–${et}`;
  if (st) return `${day} • ${st}`;
  return day;
}
function formatEventDateLabelFromEvent(ev) {
  const d = getEventStartDate(ev);
  if (!d) return { day: "", mon: "" };
  return {
    day: d.toLocaleDateString([], { day: "2-digit" }),
    mon: d.toLocaleDateString([], { month: "short" }),
  };
}
function cleanDesc(s = "") {
  return String(s || "").replace(/\*\*/g, "").replace(/\s+/g, " ").trim();
}

/* ----------------------------------------------------------------------------
   MAIN
-----------------------------------------------------------------------------*/
export default function Dashboard() {
  const navigate = useNavigate();
  const isMobile = useIsMobile(640);
  const isStandalone = useMemo(() => isStandalonePwa(), []);

  const [notifPermission, setNotifPermission] = useState(() => notificationPermissionState());
  const [notifDismissed, setNotifDismissed] = useState(
    () => localStorage.getItem("notif_prompt_dismissed") === "1"
  );
  const showNotifBanner = isMobile && isStandalone && notifPermission === "default" && !notifDismissed;
  const enableNotifications = async () => {
    const permission = await requestNotificationPermission();
    setNotifPermission(permission);
    if (permission !== "granted") return;
    const result = await subscribeToPush();
    if (result.ok) {
      toast.success("Notifications enabled for this device.");
    } else {
      toast.error(`Could not enable notifications: ${result.error}`);
    }
  };
  const dismissNotifBanner = () => {
    localStorage.setItem("notif_prompt_dismissed", "1");
    setNotifDismissed(true);
  };

  const [user, setUser] = useState(readUserFromStorage());
  const [loadingAvatar, setLoadingAvatar] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(() => Date.now());

  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [errorProjects, setErrorProjects] = useState("");

  // Events (Announcements -> Latest Calendar Events)
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [eventsError, setEventsError] = useState("");

  // Birthdays -- admin-only panel, replaces the old static "What's New" list.
  //
  // NOTE: this was briefly wired to useRolePrivilege('home_birthdays') to
  // let this be reconfigured per-role from Role & Path Management, but
  // that broke the panel for System Admin/President: role_paths is the
  // SAME flat list Sidebar.jsx's nav filter uses for page access, and
  // System Admin already has 12 unrelated page-access rows saved there
  // from before this privilege existed. useRolePrivilege can't tell "this
  // role has other things configured but never touched this key" apart
  // from "this role was deliberately denied this key" -- so it silently
  // fell back to false instead of true the moment ANY page-access row
  // existed for a role, hiding the whole panel. Reverted to the plain,
  // correct check until per-privilege state gets its own dedicated
  // table instead of sharing role_paths with page-navigation rows.
  const isAdminUser = hasRole(user?.role, ADMIN_ROLES);
  const canSeeBirthdays = isAdminUser;
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [birthdaysUpcoming, setBirthdaysUpcoming] = useState([]);
  const [loadingBirthdays, setLoadingBirthdays] = useState(true);
  const [birthdaysError, setBirthdaysError] = useState("");

  const avatarSrc = useMemo(() => {
    const base = user?.image || "/user.png";
    const sep = base.includes("?") ? "&" : "?";
    const ver = user?._avatarTs || avatarVersion;
    return `${base}${base === "/user.png" ? "" : `${sep}v=${ver}`}`;
  }, [user, avatarVersion]);

  const refreshUserFromAPI = useCallback(async () => {
    const current = readUserFromStorage();
    setUser(current);
    if (!current?.SLIITID) return;

    try {
      setLoadingAvatar(true);
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_profile.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ SLIITID: current.SLIITID }),
      });
      const data = await parseJsonSafe(res);

      if (data?.success && data.profile) {
        const next = {
          ...current,
          name: data.profile.name || current.name,
          address: data.profile.Address ?? current.address,
          contactno: data.profile.ContactNo ?? current.contactno,
        };
        const rawImg = data.profile.image || data.profile.ProfileLink;
        if (rawImg) {
          const absolute = rawImg.startsWith("http")
            ? rawImg
            : `${ABSOLUTE_HOST}${rawImg.startsWith("/") ? "" : "/"}${rawImg}`;
          if (absolute !== current.image) {
            next.image = absolute;
            next._avatarTs = Date.now();
          }
        }
        localStorage.setItem("user", JSON.stringify(next));
        setUser(next);
        if (next._avatarTs) setAvatarVersion(next._avatarTs);
      }
    } catch {
      /* ignore */
    } finally {
      setLoadingAvatar(false);
    }
  }, []);

  const loadVisibleProjects = useCallback(async () => {
    setLoadingProjects(true);
    setErrorProjects("");
    try {
      const current = readUserFromStorage();
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_visible.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ SLIITID: current?.SLIITID }),
      });
      const data = await parseJsonSafe(res);

      if (data?.success && Array.isArray(data.projects)) {
        const sorted = [...data.projects].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        setProjects(sorted);
      } else {
        setProjects([]);
        if (data?.message) setErrorProjects(data.message);
      }
    } catch {
      setErrorProjects("Unable to fetch projects.");
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Load latest events (works with ISO start or DB fields)
  const loadLatestEvents = useCallback(async () => {
    setEventsError("");
    setLoadingEvents(true);
    try {
      const body = new URLSearchParams({ limit: "5", upcoming: "1" });
      const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_events.php`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body,
      });

      const parsed = await parseJsonSafe(res);

      let list =
        (parsed && Array.isArray(parsed.events) && parsed.events) ||
        (parsed && Array.isArray(parsed.data) && parsed.data) ||
        (parsed && Array.isArray(parsed.result) && parsed.result) ||
        (Array.isArray(parsed) ? parsed : []);

      const now = Date.now();
      list = list
        .map((ev) => {
          const d = getEventStartDate(ev);
          return { ...ev, _start: d ? d.getTime() : Number.MAX_SAFE_INTEGER };
        })
        .filter((ev) => ev._start >= now)
        .sort((a, b) => a._start - b._start)
        .slice(0, 5);

      setEvents(list);
      if (!list.length) setEventsError("");
    } catch {
      setEvents([]);
      setEventsError("Unable to fetch events.");
    } finally {
      setLoadingEvents(false);
    }
  }, []);

  // Birthdays -- admin-only, server also enforces an officer-role check
  const loadBirthdays = useCallback(async () => {
    setBirthdaysError("");
    setLoadingBirthdays(true);
    try {
      const res = await authFetch(`${ABSOLUTE_HOST}/Backend/get_portal_birthdays.php`);
      const data = await parseJsonSafe(res);
      if (data?.success) {
        setBirthdaysToday(Array.isArray(data.today) ? data.today : []);
        setBirthdaysUpcoming(Array.isArray(data.upcoming) ? data.upcoming : []);
      } else {
        setBirthdaysToday([]);
        setBirthdaysUpcoming([]);
        setBirthdaysError(data?.error || "Unable to fetch birthdays.");
      }
    } catch {
      setBirthdaysToday([]);
      setBirthdaysUpcoming([]);
      setBirthdaysError("Unable to fetch birthdays.");
    } finally {
      setLoadingBirthdays(false);
    }
  }, []);

  useEffect(() => {
    refreshUserFromAPI();
    loadVisibleProjects();
    loadLatestEvents();
    if (canSeeBirthdays) loadBirthdays();

    const onStorage = (e) => {
      if (e.key === "user") {
        const next = readUserFromStorage();
        setUser(next);
        setAvatarVersion(Date.now());
        loadVisibleProjects();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { first, last } = splitName(user.name || "");
  const isAdmin = /admin/i.test(user.role || "");
  const roleLabel = isAdmin ? "System Admin" : user.role || "Member";

  const total = projects.length;
  const { active, pending, done } = useMemo(() => ({
    active: projects.filter((p) => /progress|ongoing|review|testing/i.test(p?.status || "")).length,
    pending: projects.filter((p) => /pending|draft|planned/i.test(p?.status || "")).length,
    done: projects.filter((p) => /(done|complete|completed|success|approved)/i.test(p?.status || "")).length,
  }), [projects]);

  const fadeUp = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
  };

  const openProject = (id) => navigate(`/project/${id}`);

  if (isMobile) {
    return (
      <div className="relative min-h-screen" style={{ background: "var(--bg-base, #050505)" }}>
        <Sidebar />
        <Topbar user={user} />
        <main className="relative w-full mt-14">
          <div style={{ padding: "var(--sp-4)", display: "grid", gap: "var(--sp-4)", paddingBottom: "var(--sp-7)" }}>
            {showNotifBanner && (
              <div className="panel" style={{ padding: "var(--sp-3) var(--sp-4)", display: "flex", alignItems: "center", gap: 10 }}>
                <FaBell size={14} aria-hidden="true" style={{ color: "var(--accent)", flexShrink: 0 }} />
                <p style={{ flex: 1, minWidth: 0, fontSize: 12, color: "var(--text-2)", margin: 0 }}>
                  Get notified about new events and projects{canSeeBirthdays ? ", plus board birthdays" : ""}.
                </p>
                <button onClick={enableNotifications} className="btn btn-primary btn-sm" style={{ flexShrink: 0 }}>
                  Enable
                </button>
                <button onClick={dismissNotifBanner} className="icon-btn" aria-label="Dismiss" style={{ flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Compact hero */}
            <motion.div {...fadeUp} className="panel" style={{ padding: "var(--sp-4)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  {loadingAvatar ? (
                    <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.1)" }} />
                  ) : (
                    <img
                      key={avatarSrc}
                      src={avatarSrc}
                      alt="User"
                      style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover" }}
                      onError={(e) => {
                        if (!e.currentTarget.dataset.fallback) {
                          e.currentTarget.dataset.fallback = "1";
                          e.currentTarget.src = "/user.png";
                        }
                      }}
                      draggable="false"
                    />
                  )}
                  <div
                    style={{
                      position: "absolute", bottom: -2, right: -2,
                      width: 14, height: 14, borderRadius: "50%",
                      background: "var(--success)", border: "2px solid var(--surface-1)",
                    }}
                  />
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <p style={{ fontSize: 11, color: "var(--text-3)", margin: 0 }}>@ {roleLabel}</p>
                  <h1 className="truncate" style={{ margin: "2px 0 0 0", fontSize: 18, fontWeight: 700, color: "var(--text-1)" }}>
                    Hi, {first}
                  </h1>
                </div>
              </div>

              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <button onClick={() => navigate("/create-project")} className="btn btn-primary btn-sm" style={{ flex: 1, justifyContent: "center" }}>
                  <FaPlusCircle size={12} aria-hidden="true" /> New Project
                </button>
                <button onClick={() => navigate("/club-calander")} className="btn btn-ghost btn-sm" style={{ flex: 1, justifyContent: "center" }}>
                  <FaCalendarAlt size={12} aria-hidden="true" /> Calendar
                </button>
              </div>

              {/* Stat strip: horizontal swipe instead of a squeezed 3-col grid */}
              <div
                style={{
                  marginTop: 14, display: "flex", gap: 8, overflowX: "auto",
                  scrollSnapType: "x mandatory", paddingBottom: 2,
                }}
              >
                <div className="panel tile" style={{ minWidth: 112, scrollSnapAlign: "start", flexShrink: 0, padding: "var(--sp-3)" }}>
                  <p className="tile-label">Active</p>
                  <p className="tile-value num">{active}</p>
                </div>
                <div className="panel tile" style={{ minWidth: 112, scrollSnapAlign: "start", flexShrink: 0, padding: "var(--sp-3)" }}>
                  <p className="tile-label">Total</p>
                  <p className="tile-value num">{total}</p>
                </div>
                <div className="panel tile" style={{ minWidth: 112, scrollSnapAlign: "start", flexShrink: 0, padding: "var(--sp-3)" }}>
                  <p className="tile-label">Pending</p>
                  <p className="tile-value num">{pending}</p>
                </div>
              </div>
            </motion.div>

            {/* Birthdays -- admin-only, collapsible so it doesn't eat the fold on a phone */}
            {canSeeBirthdays && (
              <details className="panel" style={{ padding: "var(--sp-4)" }} open={birthdaysToday.length > 0}>
                <summary style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700, fontSize: 13.5, cursor: "pointer" }}>
                  <FaBirthdayCake size={13} aria-hidden="true" style={{ color: "var(--accent)" }} />
                  Birthdays
                  <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                    {birthdaysToday.length > 0 && (
                      <span className="pill pill-success">{birthdaysToday.length} today</span>
                    )}
                    <FaChevronRight size={10} aria-hidden="true" className="details-chevron" style={{ color: "var(--text-3)" }} />
                  </span>
                </summary>
                {loadingBirthdays ? (
                  <div className="skeleton" style={{ height: 36, marginTop: 10 }} />
                ) : birthdaysError ? (
                  <div className="error-banner" style={{ marginTop: 8 }}>{birthdaysError}</div>
                ) : !birthdaysToday.length && !birthdaysUpcoming.length ? (
                  <p style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8 }}>No birthdays today or in the next 7 days.</p>
                ) : (
                  <ul style={{ margin: "10px 0 0 0", padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                    {birthdaysToday.map((b) => (
                      <li key={b.SLIITID} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                        <span className="pill pill-success" style={{ flexShrink: 0 }}><span className="dot" />🎂</span>
                        <span className="truncate" style={{ minWidth: 0, flex: 1 }}>{b.FullName}</span>
                      </li>
                    ))}
                    {birthdaysUpcoming.slice(0, 4).map((b) => (
                      <li key={b.SLIITID} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                        <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--text-3)", flexShrink: 0 }} />
                        <span className="truncate" style={{ minWidth: 0, flex: 1 }}>{b.FullName}</span>
                        <span className="num" style={{ fontSize: 11, color: "var(--text-3)" }}>
                          {b.DaysAway === 1 ? "tomorrow" : `in ${b.DaysAway}d`}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </details>
            )}

            {/* Latest events -- condensed to 3 */}
            <div>
              <h3 style={{ fontSize: 13.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <FaBullhorn size={12} aria-hidden="true" style={{ color: "var(--accent)" }} />
                Latest Events
              </h3>
              <div className="panel" style={{ padding: 0 }}>
                {loadingEvents ? (
                  <div style={{ padding: "var(--sp-4)" }}>
                    <div className="skeleton" style={{ height: 44, marginBottom: 10 }} />
                    <div className="skeleton" style={{ height: 44 }} />
                  </div>
                ) : eventsError ? (
                  <div className="info-banner" style={{ margin: "var(--sp-4)" }}>{eventsError}</div>
                ) : !events.length ? (
                  <div className="empty-state" style={{ padding: "var(--sp-5)" }}>
                    <p style={{ fontSize: 12.5 }}>No upcoming events.</p>
                  </div>
                ) : (
                  <div className="timeline">
                    {events.slice(0, 3).map((ev, i) => {
                      const title = ev.event_name || ev.eventName || ev.name || ev.title || "Untitled Event";
                      const when = formatEventDateTimeFromEvent(ev);
                      const { day, mon } = formatEventDateLabelFromEvent(ev);
                      return (
                        <div key={i} className="t-item">
                          <div className="t-date">
                            {day && mon ? (
                              <>
                                <span className="d num">{day}</span>
                                <span className="m">{mon}</span>
                              </>
                            ) : (
                              <FaCalendarAlt style={{ color: "var(--text-3)" }} aria-hidden="true" />
                            )}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="t-title truncate" title={title}>{title}</div>
                            {when && <div className="t-meta num" style={{ color: "var(--text-2)", marginTop: 2 }}>{when}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* My Projects -- single column, condensed */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <h3 style={{ fontSize: 13.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                  <FaFolderOpen size={12} aria-hidden="true" style={{ color: "var(--link)" }} />
                  My Projects
                </h3>
                {projects.length > 0 && (
                  <button onClick={() => navigate("/club-projects")} className="btn btn-ghost btn-sm" style={{ padding: "4px 10px", fontSize: 11.5 }}>
                    View all <FaChevronRight size={9} aria-hidden="true" />
                  </button>
                )}
              </div>

              {loadingProjects ? (
                <div style={{ display: "grid", gap: 10 }}>
                  {Array.from({ length: 3 }).map((_, i) => <ProjectSkeletonCard key={i} />)}
                </div>
              ) : errorProjects ? (
                <div className="error-banner">{errorProjects}</div>
              ) : projects.length === 0 ? (
                <div className="panel empty-state">
                  <div className="empty-icon"><FaFolderOpen aria-hidden="true" /></div>
                  <h3>No projects yet</h3>
                  <p>Create your first project to see it listed here.</p>
                  <button onClick={() => navigate("/create-project")} className="btn btn-primary" style={{ marginTop: "var(--sp-2)" }}>
                    <FaPlusCircle size={12} aria-hidden="true" /> Create Project
                  </button>
                </div>
              ) : (
                <div style={{ display: "grid", gap: 10 }}>
                  {projects.slice(0, 6).map((p, idx) => {
                    const pid = p.id ?? idx;
                    const name = (p.name || "Untitled Project").toString();
                    const status = (p.status || "In Progress").toString();
                    const pillClass = projectPillClass(status);
                    return (
                      <div
                        key={pid}
                        role="button"
                        tabIndex={0}
                        onClick={() => openProject(pid)}
                        onKeyDown={(e) => (e.key === "Enter" ? openProject(pid) : null)}
                        className="panel"
                        style={{ padding: "var(--sp-3) var(--sp-4)", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
                      >
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <h4 title={name} className="truncate" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-1)", margin: 0 }}>
                            {name}
                          </h4>
                          <span className={`pill ${pillClass}`} style={{ marginTop: 6 }}>
                            <span className="dot" />{status}
                          </span>
                        </div>
                        <FaChevronRight size={12} aria-hidden="true" style={{ color: "var(--text-3)", flexShrink: 0 }} />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen flex-col lg:flex-row" style={{ background: "var(--bg-base, #050505)" }}>
      {/* Ambient background */}
      <div
        className="pointer-events-none fixed inset-0"
        aria-hidden="true"
        style={{
          background:
            "radial-gradient(1200px 600px at 10% -10%, var(--glow-1, rgba(124,58,237,0.10)), transparent 60%), radial-gradient(1000px 500px at 100% 10%, var(--glow-2, rgba(47,111,237,0.08)), transparent 55%)",
        }}
      />
      <Sidebar />
      <Topbar user={user} />

      {/* Content container with max width for large screens */}
      <main className="relative w-full lg:ml-64 mt-14">
        <div className="px-4 sm:px-6 md:px-8 lg:px-10 py-6 max-w-7xl mx-auto">
          {/* GRID: Left content / Divider / Right rail */}
          <div className="grid grid-cols-12 gap-6">
            {/* LEFT — main column */}
            <div className="col-span-12 xl:col-span-7">
              {/* HERO */}
              <motion.div
                {...fadeUp}
                className="panel"
                style={{
                  padding: "var(--sp-6)",
                  boxShadow: "0 20px 60px -35px rgba(0,0,0,0.6)",
                }}
              >
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
                  {/* AVATAR -- untouched: real profile photo + club badge + status dot */}
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <div style={{
                      borderRadius: "50%",
                      padding: 3,
                      background: "var(--surface-2, #1c1c1c)",
                      border: "1px solid rgba(255,255,255,0.10)"
                    }}>
                      {loadingAvatar ? (
                        <div style={{
                          width: 80, height: 80, borderRadius: "50%",
                          background: "rgba(255,255,255,0.1)", animation: "pulse 1.2s infinite"
                        }}/>
                      ) : (
                        <img
                          key={avatarSrc}
                          src={avatarSrc}
                          alt="User"
                          style={{
                            width: 80, height: 80,
                            borderRadius: "50%",
                            objectFit: "cover",
                            userSelect: "none"
                          }}
                          onError={(e) => {
                            if (!e.currentTarget.dataset.fallback) {
                              e.currentTarget.dataset.fallback = "1";
                              e.currentTarget.src = "/user.png";
                            }
                          }}
                          draggable="false"
                        />
                      )}
                    </div>

                    {/* org logo dot */}
                    <div style={{
                      position:"absolute", top:-4, left:-4,
                      width:20, height:20, borderRadius:"50%",
                      background:"var(--surface-2, #1c1c1c)",
                      border:"2px solid var(--surface-2, #1c1c1c)",
                      display:"grid", placeItems:"center",
                      overflow:"hidden"
                    }}>
                      <img src="/club.png" alt="Org" style={{ width:"100%", height:"100%", objectFit:"cover", borderRadius:"50%" }}/>
                    </div>

                    {/* active status dot */}
                    <div style={{
                      position:"absolute", bottom:-4, right:-4,
                      width:20, height:20, borderRadius:"50%",
                      background:"var(--success)",
                      border:"2px solid var(--surface-2, #1c1c1c)",
                      display:"grid", placeItems:"center",
                      boxShadow:"0 0 14px rgba(16,185,129,.8)"
                    }}>
                      <FaCheckCircle style={{ color:"#fff", fontSize:11 }}/>
                    </div>
                  </div>

                  {/* TEXTS -- name/greeting untouched */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <p style={{ fontSize:12, letterSpacing:0.5, color:"var(--text-2)", margin:0 }}>
                      @ {roleLabel}
                    </p>

                    <h1 style={{
                      margin:"6px 0 0 0",
                      fontSize:"1.9rem",
                      lineHeight:1.2,
                      fontFamily:'"Inter", "SF Pro Display", system-ui, sans-serif',
                      fontWeight:300, // light for "Welcome back,"
                      color:"var(--text-1)"
                    }}>
                      Welcome back,&nbsp;
                      <span style={{
                        fontWeight:700, // bold for name
                        color:"var(--text-1)"
                      }}>
                        {first} {last}
                      </span>
                    </h1>

                    {/* ACTION BUTTONS -- shared .btn system */}
                    <div style={{ marginTop:16, display:"flex", flexWrap:"wrap", gap:10 }}>
                      <button onClick={() => navigate("/create-project")} className="btn btn-primary">
                        <FaPlusCircle size={13} aria-hidden="true" />
                        Create Project
                      </button>
                      <button onClick={() => navigate("/club-calander")} className="btn btn-ghost">
                        <FaCalendarAlt size={13} aria-hidden="true" />
                        Project Calendar
                      </button>
                    </div>

                    {/* PRIORITY STRIP: one wide hero metric beside two standard tiles,
                        not three identical boxes -- surfaces the thing that matters
                        (how much of your work is actually active) ahead of raw counts. */}
                    {total > 0 ? (
                      <div className="priority" style={{ marginTop: "var(--sp-5)", marginBottom: 0 }}>
                        <div className="panel hero-metric" style={{ minWidth: 0 }}>
                          <div className="hero-top">
                            <span className="eyebrow">Active Projects</span>
                            <div
                              className="ring"
                              role="img"
                              aria-label={`${active} of ${total} projects currently active`}
                              data-pct={`${active}/${total}`}
                              style={{ "--pct": Math.round((active / total) * 100) }}
                            />
                          </div>
                          <p className="hero-sub" style={{ marginTop: 2 }}>
                            <span className="num" style={{ color: "var(--text-1)", fontWeight: 700, fontSize: 15 }}>{active}</span>
                            {" "}of{" "}
                            <span className="num" style={{ color: "var(--text-2)" }}>{total}</span>
                            {" "}projects active
                          </p>
                          <p className="truncate" style={{ fontSize: 12, color: "var(--text-3)", marginTop: 2 }}>
                            {projects.slice(0, 2).map((p) => p.name).filter(Boolean).join(" · ") || "No projects yet"}
                          </p>
                        </div>

                        <div className="panel tile">
                          <div>
                            <p className="tile-label">Total Projects</p>
                            <p className="tile-value num">{total}</p>
                          </div>
                          <p className={`tile-trend ${done > 0 ? "trend-up" : "trend-flat"}`}>
                            {done > 0 ? (
                              <>
                                <FaCheckCircle size={10} aria-hidden="true" />
                                <span className="num">{done}</span> completed
                              </>
                            ) : (
                              "No completions yet"
                            )}
                          </p>
                        </div>

                        <div className="panel tile">
                          <div>
                            <p className="tile-label">Pending</p>
                            <p className="tile-value num">{pending}</p>
                          </div>
                          <p className={`tile-trend ${pending > 0 ? "trend-warn" : "trend-flat"}`}>
                            {pending > 0 ? "Needs attention" : "All caught up"}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        style={{
                          marginTop: "var(--sp-5)",
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                          gap: "var(--sp-4)",
                        }}
                      >
                        <div className="panel tile">
                          <p className="tile-label">Total Projects</p>
                          <p className="tile-value num">{total}</p>
                        </div>
                        <div className="panel tile">
                          <p className="tile-label">Pending</p>
                          <p className="tile-value num">{pending}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* Latest Calendar Events */}
              <div style={{ marginTop: "var(--sp-7)" }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, marginBottom: "var(--sp-3)" }}>
                  <FaBullhorn size={13} aria-hidden="true" style={{ color: "var(--accent)" }} />
                  Latest Calendar Events
                </h3>

                <div className="panel" style={{ padding: 0 }}>
                  {loadingEvents ? (
                    <div style={{ display: "grid", gap: 1, padding: "var(--sp-4)" }}>
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="skeleton" style={{ height: 54, marginBottom: 12 }} />
                      ))}
                    </div>
                  ) : eventsError ? (
                    <div className="info-banner" style={{ margin: "var(--sp-4)" }}>{eventsError}</div>
                  ) : !events.length ? (
                    <div className="empty-state">
                      <div className="empty-icon"><FaCalendarAlt aria-hidden="true" /></div>
                      <h3>No upcoming events</h3>
                      <p>Check back soon for new events on the club calendar.</p>
                    </div>
                  ) : (
                    <div className="timeline">
                      {events.map((ev, i) => {
                        const title = ev.event_name || ev.eventName || ev.name || ev.title || "Untitled Event";
                        const when = formatEventDateTimeFromEvent(ev);
                        const { day, mon } = formatEventDateLabelFromEvent(ev);
                        const location = ev.location || ev.venue || "";
                        const desc = cleanDesc(ev.description || "");

                        return (
                          <div key={i} className="t-item">
                            <div className="t-date">
                              {day && mon ? (
                                <>
                                  <span className="d num">{day}</span>
                                  <span className="m">{mon}</span>
                                </>
                              ) : (
                                <FaCalendarAlt style={{ color: "var(--text-3)" }} aria-hidden="true" />
                              )}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div className="t-title truncate" title={title}>{title}</div>
                              <div className="t-meta" style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 4 }}>
                                {when && <span className="num" style={{ color: "var(--text-2)" }}>{when}</span>}
                                {location && <span>{location}</span>}
                              </div>
                              {desc && (
                                <p style={{
                                  marginTop: 6,
                                  marginBottom: 0,
                                  fontSize: 12,
                                  lineHeight: 1.5,
                                  color: "var(--text-2)",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden"
                                }}>
                                  {desc}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* My Projects */}
              <div style={{ marginTop: "var(--sp-7)" }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, marginBottom: "var(--sp-3)" }}>
                  <FaFolderOpen size={13} aria-hidden="true" style={{ color: "var(--link)" }} />
                  My Projects
                </h3>

                {loadingProjects ? (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "var(--sp-4)"
                  }}>
                    {Array.from({ length: 8 }).map((_, i) => <ProjectSkeletonCard key={i} />)}
                  </div>
                ) : errorProjects ? (
                  <div className="error-banner">
                    {errorProjects}
                  </div>
                ) : projects.length === 0 ? (
                  <div className="panel empty-state">
                    <div className="empty-icon"><FaFolderOpen aria-hidden="true" /></div>
                    <h3>No projects yet</h3>
                    <p>Create your first project to see it listed here.</p>
                    <button onClick={() => navigate("/create-project")} className="btn btn-primary" style={{ marginTop: "var(--sp-2)" }}>
                      <FaPlusCircle size={12} aria-hidden="true" /> Create Project
                    </button>
                  </div>
                ) : (
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                    gap: "var(--sp-4)"
                  }}>
                    {projects.map((p, idx) => {
                      const pid = p.id ?? idx;
                      const name = (p.name || "Untitled Project").toString();
                      const desc = (p.description || "").toString();
                      const status = (p.status || "In Progress").toString();
                      const phase = Number.isFinite(+p.phase_count) && +p.phase_count > 0 ? +p.phase_count : 1;
                      const dateStr = p.updated_at
                        ? new Date(p.updated_at).toLocaleDateString()
                        : (p.created_at ? new Date(p.created_at).toLocaleDateString() : "");
                      const pillClass = projectPillClass(status);

                      return (
                        <div
                          key={pid}
                          role="button"
                          tabIndex={0}
                          onClick={() => openProject(pid)}
                          onKeyDown={(e) => (e.key === "Enter" ? openProject(pid) : null)}
                          className="panel"
                          style={{
                            padding: "var(--sp-4)",
                            cursor: "pointer",
                            transition: "border-color 160ms ease, background-color 160ms ease, transform 160ms ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = "var(--border-strong)";
                            e.currentTarget.style.background = "var(--surface-2)";
                            e.currentTarget.style.transform = "translateY(-2px)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = "var(--border)";
                            e.currentTarget.style.background = "var(--surface-1)";
                            e.currentTarget.style.transform = "translateY(0)";
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                <h4 title={name} className="truncate" style={{ fontSize: 14, fontWeight: 700, color: "var(--text-1)", margin: 0 }}>
                                  {name}
                                </h4>
                                <span style={{
                                  display: "inline-flex", alignItems: "center", flexShrink: 0,
                                  padding: "2px 8px", borderRadius: 999, fontSize: 10.5,
                                  background: "var(--surface-2)", color: "var(--text-2)",
                                  border: "1px solid var(--border)"
                                }}>
                                  Phase <span className="num" style={{ marginLeft: 3 }}>{phase}</span>
                                </span>
                              </div>

                              <div style={{ marginTop: 8, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                                <span className={`pill ${pillClass}`}>
                                  <span className="dot" />
                                  {status}
                                </span>
                                {dateStr && (
                                  <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                                    {p.updated_at ? "Updated" : "Created"} <span className="num">{dateStr}</span>
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="icon-btn" style={{ background: "var(--surface-2)", borderColor: "var(--border)", flexShrink: 0 }}>
                              <FaChevronRight size={12} aria-hidden="true" />
                            </div>
                          </div>

                          {desc && (
                            <p style={{
                              marginTop: 10, marginBottom: 0, fontSize: 12.5, lineHeight: 1.45,
                              color: "var(--text-2)",
                              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden"
                            }}>
                              {desc}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Divider (only on xl) */}
            <div className="hidden xl:block col-span-12 xl:col-span-1">
              <div style={{ height: "100%", width: 1, margin: "0 auto", background: "linear-gradient(to bottom, transparent, var(--border), transparent)" }} />
            </div>

            {/* RIGHT RAIL */}
            <div className="col-span-12 xl:col-span-4">
              {/* Birthdays -- officers/admins only */}
              {canSeeBirthdays && (
                <div className="panel" style={{ padding: "var(--sp-5)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                    <FaBirthdayCake size={14} aria-hidden="true" style={{ color: "var(--accent)" }} />
                    <h3 style={{ margin: 0, fontWeight: 700, fontSize: 14.5 }}>Birthdays</h3>
                  </div>

                  {loadingBirthdays ? (
                    <div style={{ display: "grid", gap: 8 }}>
                      {Array.from({ length: 2 }).map((_, i) => (
                        <div key={i} className="skeleton" style={{ height: 36 }} />
                      ))}
                    </div>
                  ) : birthdaysError ? (
                    <div className="error-banner" style={{ marginTop: 8 }}>{birthdaysError}</div>
                  ) : !birthdaysToday.length && !birthdaysUpcoming.length ? (
                    <p style={{ fontSize: 12.5, color: "var(--text-3)", marginTop: 8 }}>No birthdays today or in the next 7 days.</p>
                  ) : (
                    <>
                      {birthdaysToday.length > 0 && (
                        <div style={{ marginTop: 4 }}>
                          <div className="eyebrow" style={{ marginBottom: 8 }}>Today</div>
                          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                            {birthdaysToday.map((b) => (
                              <li key={b.SLIITID} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                                <span className="pill pill-success" style={{ flexShrink: 0 }}><span className="dot" />🎂</span>
                                <span style={{ minWidth: 0, flex: 1 }} className="truncate" title={b.FullName}>{b.FullName}</span>
                                <span style={{ fontSize: 11, color: "var(--text-3)" }}>{b.Role}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {birthdaysUpcoming.length > 0 && (
                        <div style={{ marginTop: birthdaysToday.length ? 14 : 4 }}>
                          <div className="eyebrow" style={{ marginBottom: 8 }}>Coming up</div>
                          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: 8 }}>
                            {birthdaysUpcoming.slice(0, 5).map((b) => (
                              <li key={b.SLIITID} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13 }}>
                                <span style={{ width: 6, height: 6, borderRadius: 999, background: "var(--text-3)", flexShrink: 0 }} />
                                <span style={{ minWidth: 0, flex: 1 }} className="truncate" title={b.FullName}>{b.FullName}</span>
                                <span className="num" style={{ fontSize: 11, color: "var(--text-3)" }}>
                                  {b.DaysAway === 1 ? "tomorrow" : `in ${b.DaysAway}d`}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Upcoming Projects */}
              <div className="panel" style={{ marginTop: "var(--sp-5)", padding: "var(--sp-5)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FaCalendarAlt size={14} aria-hidden="true" style={{ color: "var(--accent)" }} />
                  <h3 style={{ margin: 0, fontWeight: 700, fontSize: 14.5 }}>Upcoming Projects</h3>
                </div>

                <div className="timeline" style={{ marginTop: "var(--sp-2)" }}>
                  <div className="t-item">
                    <div className="t-date">
                      <FaCalendarAlt aria-hidden="true" style={{ color: "var(--text-3)" }} />
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="t-title">No Upcoming Projects</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
